import { Loading } from "@components/LoadingIndicator";
import {
  PaymentProcessRouteName,
  paymentProcessRouteNameToPathMap,
} from "@components/donate/DonateV3/PaymentProcess/components/PaymentProcessLink";
import { DonateFormContext } from "@components/donate/DonateV3/PaymentProcess/useDonateFormContext";
import {
  validateAmountAndFrequency,
  validateCommentText,
  validateCreditsOnlyAmount,
  validateNameEmail,
} from "@components/donate/DonateV3/PaymentProcess/validators";
import {
  CreateOrUpdateDonationResult,
  DonateModalAction,
  DonateFormType,
  DONATE_FORM_ERROR,
} from "@components/donate/DonateV3/types";
import { redirectToLogin } from "@components/donate/helpers";
import {
  Elements,
  ExpressCheckoutElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import type {
  PaymentMethod,
  StripeElementsOptions,
  StripeExpressCheckoutElementAvailablePaymentMethodsChangeEvent,
  StripeExpressCheckoutElementClickEvent,
  StripeExpressCheckoutElementConfirmEvent,
  StripeExpressCheckoutElementOptions,
  StripeError,
} from "@stripe/stripe-js";
import React, { useCallback, useMemo, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { currencyValueCodec } from "@every.org/common/src/codecs/currency";
import { decodeOrThrow } from "@every.org/common/src/codecs/index";
import {
  Currency,
  DonationFrequencyToUserFacingTextMap,
  PaymentRequestType,
} from "@every.org/common/src/entity/types";
import { currencyValueToMinimumDenominationAmount } from "@every.org/common/src/helpers/currency";

import { ContextNonprofit } from "src/context/NonprofitsContext/types";
import {
  PaymentRequestReadyStatus,
  WALLET_ONLY_PAYMENT_METHODS,
} from "src/context/PaymentRequestContext";
import { getMessageForError } from "src/errors";
import { useEdoRouter } from "src/hooks/useEdoRouter";
import { trackEvent } from "src/utility/analytics";
import { logger } from "src/utility/logger";
import { useStripePromise } from "src/utility/stripe";

/**
 * Matches the height of a large Button (2 * spacing.m + TextSize.m), which is
 * what the old hand-styled Apple Pay button rendered at.
 */
const WALLET_BUTTON_HEIGHT_PX = 48;

// Stripe renders the wallet button in its own iframe, so a click never
// reaches the document-level data-tname tracker in src/utility/analytics -
// the element's click handler reports the same event by hand instead.
const WALLET_BUTTON_TNAME = "paymentRequestButton";

interface PaymentRequestButtonProps {
  donateAction: DonateModalAction;
  form: UseFormReturn<DonateFormType>;
  formContext: DonateFormContext;
  nonprofit: ContextNonprofit;
  submitDonation: (
    formValues: DonateFormType
  ) => Promise<CreateOrUpdateDonationResult | undefined>;
  handleConfirmedDonation: (result: CreateOrUpdateDonationResult) => boolean;
}

enum PaymentRequestStatus {
  NOT_STARTED,
  /**
   * From the button click until the wallet hands over a PaymentMethod
   * (covers the open wallet sheet)
   */
  PENDING,
  /**
   * The wallet sheet has closed and the donation (PaymentIntent
   * create/confirm + /donate) is in flight
   */
  SUBMITTING,
  SUCCESS,
}

const WALLET_ERROR_MESSAGE =
  "Unable to verify this payment method. Please try again.";

// TODO #8478: parametrize currency?
const WalletButton: React.FCC<
  PaymentRequestButtonProps & { amountInMinDenom: number }
> = ({
  donateAction,
  form,
  formContext,
  submitDonation,
  handleConfirmedDonation,
  nonprofit,
  amountInMinDenom,
}) => {
  const { paymentRequestInitializer, availableGivingCredit } = formContext;
  const { isApplePay } = paymentRequestInitializer;
  const stripe = useStripe();
  const elements = useElements();
  const [paymentRequestStatus, setPaymentRequestStatus] =
    useState<PaymentRequestStatus>(PaymentRequestStatus.NOT_STARTED);
  // The context-level probe already said a wallet is available; this only
  // hides the element if Stripe ends up rendering no button here anyway.
  const [hasWalletButton, setHasWalletButton] = useState(true);
  const navigate = useNavigate();
  const router = useEdoRouter();

  const {
    amount,
    frequency,
    privateNote,
    commentText,
    paymentMethod,
    firstName,
    lastName,
    email,
  } = form.watch();
  const { successUrl, minValue, maxValue, shorten } = formContext;

  const elementOptions = useMemo<StripeExpressCheckoutElementOptions>(
    () => ({
      buttonType: { applePay: "donate", googlePay: "donate" },
      buttonTheme: { applePay: "black", googlePay: "black" },
      buttonHeight: WALLET_BUTTON_HEIGHT_PX,
      emailRequired: true,
      // One wallet at a time, matching the single "Apple Pay"/"Google Pay"
      // payment option the picker offers.
      paymentMethods: {
        ...WALLET_ONLY_PAYMENT_METHODS,
        applePay: isApplePay ? "auto" : "never",
        googlePay: isApplePay ? "never" : "auto",
      },
    }),
    [isApplePay]
  );

  const onAvailablePaymentMethodsChange = useCallback(
    ({
      paymentMethods,
    }: StripeExpressCheckoutElementAvailablePaymentMethodsChangeEvent) =>
      setHasWalletButton(!!paymentMethods),
    []
  );
  const onLoadError = useCallback(({ error }: { error: StripeError }) => {
    logger.error({
      message: "Express Checkout wallet button failed to load",
      data: { type: error.type, code: error.code, message: error.message },
    });
    setHasWalletButton(false);
  }, []);

  const onClick = useCallback(
    (event: StripeExpressCheckoutElementClickEvent) => {
      trackEvent("Click", {
        tname: WALLET_BUTTON_TNAME,
        tnamePath: WALLET_BUTTON_TNAME,
      });
      const validated =
        validateAmountAndFrequency({
          amount: amount?.toString() || "",
          frequency,
          minValue,
          maxValue,
          shorten,
          setError: form.setError,
        }) &&
        validateCreditsOnlyAmount({
          amount: amount?.toString() || "",
          availableGivingCredit,
          paymentMethod,
          shorten,
          setError: form.setError,
        }) &&
        validateCommentText(
          privateNote,
          form.setError,
          "privateNote",
          nonprofit.metadata?.privateNoteLimit
        ) &&
        validateCommentText(commentText, form.setError, "commentText") &&
        validateNameEmail({
          firstName,
          lastName,
          email,
          setError: form.setError,
        });

      if (
        !validated ||
        donateAction === DonateModalAction.UPDATE ||
        !frequency
      ) {
        // Keeps the wallet sheet closed; validators have already put the
        // error on the form.
        event.reject();
        setPaymentRequestStatus(PaymentRequestStatus.NOT_STARTED);
        return;
      }
      form.clearErrors(DONATE_FORM_ERROR);
      setPaymentRequestStatus(PaymentRequestStatus.PENDING);
      event.resolve({
        lineItems: [
          {
            name: `${DonationFrequencyToUserFacingTextMap[frequency]} gift for ${nonprofit.name}`,
            amount: amountInMinDenom,
          },
        ],
      });
    },
    [
      amount,
      amountInMinDenom,
      availableGivingCredit,
      commentText,
      donateAction,
      email,
      firstName,
      form,
      frequency,
      lastName,
      maxValue,
      minValue,
      nonprofit.metadata?.privateNoteLimit,
      nonprofit.name,
      paymentMethod,
      privateNote,
      shorten,
    ]
  );

  const onCancel = useCallback(() => {
    setPaymentRequestStatus((status) =>
      status === PaymentRequestStatus.PENDING
        ? PaymentRequestStatus.NOT_STARTED
        : status
    );
  }, []);

  const onConfirm = useCallback(
    async (event: StripeExpressCheckoutElementConfirmEvent) => {
      const failOnSheet = (message?: string) => {
        event.paymentFailed({ reason: "fail", message });
        form.setError(DONATE_FORM_ERROR, {
          type: "string",
          message: message || WALLET_ERROR_MESSAGE,
        });
        setPaymentRequestStatus(PaymentRequestStatus.NOT_STARTED);
      };
      if (!stripe || !elements) {
        logger.error({
          message: "Wallet payment was confirmed, but stripe wasn't loaded",
        });
        failOnSheet();
        return;
      }
      // Turn the wallet's token into a PaymentMethod without confirming
      // anything yet - this closes the sheet, and the PaymentMethod id then
      // rides through the same pre-authorize-then-capture path a saved card
      // uses (see confirmExistingPaymentMethod). The sheet's ~30s completion
      // timeout can't cover the whole donation round-trip (guest creation +
      // PaymentIntent create/confirm + /donate), and a 3DS challenge can't
      // render behind a still-open sheet. Any failure from here on surfaces
      // as a form error on the page instead of on the sheet.
      let paymentMethod: PaymentMethod;
      try {
        const { error: submitError } = await elements.submit();
        if (submitError) {
          failOnSheet(submitError.message);
          return;
        }
        const created = await stripe.createPaymentMethod({ elements });
        if (created.error || !created.paymentMethod) {
          failOnSheet(created.error?.message);
          return;
        }
        paymentMethod = created.paymentMethod;
      } catch (e) {
        // Anything thrown while the wallet sheet is open must be reported
        // through paymentFailed - an exception escaping this handler makes
        // Google Pay report a DEVELOPER_ERROR instead of showing the donor
        // anything useful.
        logger.error({
          message: "Wallet sheet failed before a PaymentMethod was created",
          error: e,
        });
        failOnSheet(e instanceof Error ? e.message : undefined);
        return;
      }

      const payerEmail =
        event.billingDetails?.email || paymentMethod.billing_details.email;
      const payerName =
        event.billingDetails?.name || paymentMethod.billing_details.name;
      if (payerEmail) {
        // Do we need to set both fields?
        form.setValue("email", payerEmail);
        form.setValue("payerEmail", payerEmail);
      }
      if (payerName) {
        form.setValue("payerName", payerName);
      }
      form.setValue("paymentRequestPaymentMethodId", paymentMethod.id);
      setPaymentRequestStatus(PaymentRequestStatus.SUBMITTING);
      try {
        const result = await submitDonation({
          ...form.getValues(),
          paymentTab:
            event.expressPaymentType === "apple_pay"
              ? PaymentRequestType.APPLE_PAY
              : PaymentRequestType.GOOGLE_PAY,
          walletType: paymentMethod.card?.wallet?.type,
        });
        if (!result) {
          setPaymentRequestStatus(PaymentRequestStatus.NOT_STARTED);
          return;
        }
        setPaymentRequestStatus(PaymentRequestStatus.SUCCESS);
        if (!handleConfirmedDonation(result)) {
          navigate(
            paymentProcessRouteNameToPathMap[PaymentProcessRouteName.THANK_YOU]
          );
        }
      } catch (e) {
        if (e instanceof Error) {
          if (
            payerEmail &&
            e.message &&
            // TODO Return more granular statuses for donations so we're not
            // relying on parsing an API error
            (e.message as string).includes("already registered")
          ) {
            redirectToLogin({
              router,
              amount: amount?.toString() || "",
              frequency,
              userEmail: payerEmail,
              nonprofitSlug: nonprofit.primarySlug,
              successUrl,
            });
          }
          form.setError(DONATE_FORM_ERROR, getMessageForError(e));
          setPaymentRequestStatus(PaymentRequestStatus.NOT_STARTED);
        }
      }
    },
    [
      stripe,
      elements,
      form,
      submitDonation,
      handleConfirmedDonation,
      navigate,
      router,
      amount,
      frequency,
      nonprofit.primarySlug,
      successUrl,
    ]
  );

  if (!hasWalletButton) {
    return <React.Fragment />;
  }

  const submitting = paymentRequestStatus === PaymentRequestStatus.SUBMITTING;

  return (
    <div css={{ width: "100%" }}>
      {submitting && <Loading />}
      {/* Kept mounted (just hidden) while submitting so the element doesn't
          have to reload if the donation fails and the donor retries. */}
      <div hidden={submitting}>
        <ExpressCheckoutElement
          options={elementOptions}
          onClick={onClick}
          onConfirm={onConfirm}
          onCancel={onCancel}
          onAvailablePaymentMethodsChange={onAvailablePaymentMethodsChange}
          onLoadError={onLoadError}
        />
      </div>
    </div>
  );
};

/**
 * Apple Pay / Google Pay donate button, built on Stripe's Express Checkout
 * Element. Mounted in Stripe's "deferred" Elements mode (same as
 * AddCreditCard) - no PaymentIntent needs to exist for the button to render.
 */
export const PaymentRequestButton: React.FCC<PaymentRequestButtonProps> = (
  props
) => {
  const { readyStatus } = props.formContext.paymentRequestInitializer;
  const stripePromise = useStripePromise();
  const amountInMinDenom = currencyValueToMinimumDenominationAmount({
    value: decodeOrThrow(currencyValueCodec, {
      amount: props.formContext.amountToCharge,
      currency: Currency.USD,
    }),
  });
  const elementsOptions = useMemo<StripeElementsOptions>(
    () => ({
      mode: "payment",
      amount: amountInMinDenom,
      currency: "usd",
      paymentMethodTypes: ["card"],
      // Required to call stripe.createPaymentMethod from the confirm handler
      // (instead of confirmPayment) - see onConfirm.
      paymentMethodCreation: "manual",
    }),
    [amountInMinDenom]
  );

  if (
    readyStatus === PaymentRequestReadyStatus.UNINITIALIZED ||
    !stripePromise
  ) {
    return <Loading />;
  }
  // Stripe rejects zero-amount Elements in payment mode; a credits-only
  // donation never renders this button anyway (see DonationConfirmation).
  if (
    readyStatus === PaymentRequestReadyStatus.UNABLE ||
    amountInMinDenom <= 0
  ) {
    return <React.Fragment />;
  }

  return (
    <Elements stripe={stripePromise} options={elementsOptions}>
      <WalletButton {...props} amountInMinDenom={amountInMinDenom} />
    </Elements>
  );
};
